import { createFormDataRequest } from "@/helpers/FormHelpers";
import Company, {
  CompanyFiltersSend,
  CompanyFormSend,
  DispatchCompaniesFormSend,
  DispatchCompaniesResponse,
} from "@/interfaces/Company";
import { IPaginateResponse } from "@/interfaces/Fetch";
import api from "../api";

export const companiesApi = api.injectEndpoints({
  endpoints: (build) => ({
    getCompanies: build.query<IPaginateResponse<Company>, CompanyFiltersSend>({
      query: (data) => {
        const params = createFormDataRequest(data);
        return {
          url: "/companies",
          params,
        };
      },
      providesTags: (result, _error, _arg) =>
        result
          ? [
              ...result.data.map(({ id }) => ({
                type: "Companies" as const,
                id,
              })),
              { type: "Companies", id: "LIST" },
            ]
          : [],
    }),
    getCompany: build.query<Company, string>({
      query: (id) => `/companies/${id}`,
      providesTags: (_result, _error, id) => [{ type: "Companies", id }],
    }),
    storeCompany: build.mutation<Company, CompanyFormSend>({
      query: (body) => ({
        url: `/companies`,
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: "CommercialBooking", id: "LIST" },
        { type: "Companies", id: "LIST" },
      ],
    }),
    updateCompany: build.mutation<Company, [string, Partial<CompanyFormSend>]>({
      query: ([id, body]) => ({
        url: `/companies/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_, __, [id]) => [
        { type: "CommercialBooking", id: "LIST" },
        { type: "Companies", id },
        { type: "CompanyCourses", id: "LIST" },
        { type: "Meetings", id: "LIST" },
      ],
    }),
    deleteCompany: build.mutation<void, string>({
      query: (id) => ({
        url: `/companies/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        { type: "CommercialBooking", id: "LIST" },
        { type: "Companies", id: "LIST" },
      ],
    }),
    dispatchCompanies: build.mutation<
      DispatchCompaniesResponse,
      DispatchCompaniesFormSend
    >({
      query: (body) => ({
        url: `/companies/dispatch`,
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: "CommercialBooking", id: "LIST" },
        { type: "Companies", id: "LIST" },
      ],
    }),
  }),
  overrideExisting: true,
});

export const {
  useGetCompaniesQuery,
  useLazyGetCompaniesQuery,
  useGetCompanyQuery,
  useStoreCompanyMutation,
  useDeleteCompanyMutation,
  useUpdateCompanyMutation,
  useDispatchCompaniesMutation,
} = companiesApi;
